import type { ReactNode } from 'react';
import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import { toast } from 'sonner';

import {
  createPitchTrackerConfig,
  native,
  usePitchSession,
} from '@muzly/shared';
import type {
  DiagnosticsSnapshot,
  PitchAlgorithm,
  PitchAnalysisConfig,
  PitchTrackerConfig,
  PitchTrackerModule,
  SessionStatus,
} from '@muzly/shared';

import {
  type Liveness,
  type SessionCommand,
  type SessionState,
  deriveLiveness,
} from './sessionReconcile';
import {
  type BrowserSource,
  type MicFailure,
  createBrowserSourceModule,
} from '../../../audio';

/**
 * 세션 제어 상태를 하나로 모으는 컨텍스트(#3, #21).
 *
 * 세션 머신·전이·네이티브 배선은 재사용 라이브러리 `usePitchSession`이 소유한다.
 * 이 컨텍스트는 그 훅을 한 번 구동해 풀 패널(SessionControlPanel)과 모바일 sticky
 * 바(SessionStickyBar)가 **같은** 세션을 공유하게 하고, 패널 표시용 파생값
 * (라이브 인디케이터 타이밍·알고리즘 선택)만 얹는다.
 *
 * 멀티클릭 차단: 훅의 동기 가드(전이 중 모든 명령 무시)가 1차 방어, UI는
 * `isTransitioning`으로 버튼을 비활성/“처리 중”으로 잠가 2차 방어한다.
 *
 * 불변식: 세션 구동은 모두 훅을 통해 `native`(RPC) 경유, 네이티브 셸이 없으면
 * mock PitchTracker + PracticeBridge로 같은 흐름을 재현(브라우저에서도 동작).
 */

export const DEFAULT_CAPTURE_RATE =
  createPitchTrackerConfig().audio.captureSampleRate;
/** 라이브 인디케이터 갱신 주기. */
const TICK_MS = 500;
/** 슬라이더 드래그가 updateConfig를 난사하지 않게 하는 최소 간격. */
const CONFIG_DEBOUNCE_MS = 200;

/** 머신의 8상태를 패널이 쓰는 3상태로 압축(전이는 직전 의도 쪽으로 본다). */
const SESSION_BY_STATUS: Record<SessionStatus, SessionState> = {
  idle: 'idle',
  starting: 'idle',
  running: 'running',
  pausing: 'running',
  paused: 'paused',
  resuming: 'running',
  stopping: 'running',
  error: 'idle',
};

export const DEFAULT_ANALYSIS = createPitchTrackerConfig().analysis;

// output은 프로덕션 기본값 그대로 둔다 — 예전엔 여기서 `eventIntervalMs: 50`을 덮어썼는데,
// 네이티브는 그 값을 스로틀로 쓰지 않으므로 콘솔만 실제와 다른 주기를 표시하게 된다.
//
// `analysis`는 **튜닝 패널이 고른 값**을 그대로 받는다. 예전엔 여기서 프로덕션 기본값을
// 다시 만들어 `algorithm`만 덮었는데, 그러면 튜닝해 둔 값이 **다음 start에서 조용히
// 기본값으로 되돌아간다**(실기기 포함). 튜닝 상태가 패널 안에 살던 것이 원인이라, 상태를
// 이 provider로 올려 세션 시작과 런타임 적용이 같은 값을 보게 한다.
const buildConfig = (analysis: PitchAnalysisConfig): PitchTrackerConfig => {
  const base = createPitchTrackerConfig();
  return { ...base, analysis };
};

interface Capabilities {
  pause: boolean;
  resume: boolean;
  /** 알고리즘은 대기(idle)에서만 변경 가능, 고른 값이 다음 시작에 적용. */
  algorithm: boolean;
  /** 소스도 대기에서만 — 마이크는 사용자 제스처 안에서 열려야 하므로 start 시점에 잡는다. */
  source: boolean;
}

interface SessionControlValue {
  isNative: boolean;
  algorithm: PitchAlgorithm;
  setAlgorithm: (algorithm: PitchAlgorithm) => void;
  /** 튜닝 패널이 고른 분석 파라미터. 세션 start와 런타임 적용이 **같은** 값을 본다. */
  analysis: PitchAnalysisConfig;
  setAnalysis: (next: PitchAnalysisConfig) => void;
  /**
   * 브라우저에서 프레임이 어디서 오나. 네이티브에서는 의미 없다(항상 실제 엔진).
   * `'mic'`은 실제 목소리지만 **추정기가 JS ACF**라 엔진이 아니다 — mock은 임계값에
   * 걸릴 것이 없어 튜닝이 눈에 안 보이므로, 책상에서 튜닝하려면 이 소스가 필요하다.
   */
  source: BrowserSource;
  setSource: (next: BrowserSource) => void;
  /** 마이크를 못 열었으면 왜인지. 성공/미시도면 null. */
  micError: MicFailure | null;
  sessionState: SessionState;
  captureSampleRate: number | null;
  fps: number;
  liveness: Liveness;
  can: Capabilities;
  isStopped: boolean;
  /** 전이 중(starting/stopping/pausing/resuming) — 모든 제어 버튼 잠금. */
  isTransitioning: boolean;
  run: (command: SessionCommand) => void;
  /**
   * 지금 돌고 있는 mock 모듈의 진단 스냅샷(브라우저 전용, 없으면 null).
   * 진단 패널이 **자기만의 모듈**을 새로 만들어 폴링하던 것을 대신한다 — 그 인스턴스는
   * 시작된 적이 없어 세션이 뭘 하든 항상 같은 값을 냈다.
   */
  getDiagnostics: () => Promise<DiagnosticsSnapshot> | null;
}

const SessionControlContext = createContext<SessionControlValue | null>(null);

export const SessionControlProvider = ({
  children,
}: {
  children: ReactNode;
}) => {
  const [analysis, setAnalysis] =
    useState<PitchAnalysisConfig>(DEFAULT_ANALYSIS);
  const algorithm = analysis.algorithm as PitchAlgorithm;
  const setAlgorithm = (next: PitchAlgorithm): void =>
    setAnalysis((prev) => ({ ...prev, algorithm: next }));
  const config = useMemo(() => buildConfig(analysis), [analysis]);
  const [source, setSource] = useState<BrowserSource>('mock');
  const [micError, setMicError] = useState<MicFailure | null>(null);
  // `createModule`은 mount-once로 한 번만 불리므로 소스는 ref로 넘긴다 — 모듈이 매
  // `start()`에서 이 값을 읽어 그 세션의 소스를 정한다(세션을 다시 시작하면 전환된다).
  const sourceRef = useRef<BrowserSource>(source);
  sourceRef.current = source;

  // 세션이 실제로 쓰는 모듈 — 진단 패널과 튜닝 적용이 같은 인스턴스를 봐야 한다.
  const moduleRef = useRef<PitchTrackerModule | null>(null);
  const session = usePitchSession({
    config,
    createModule: () => {
      const module = createBrowserSourceModule({
        getSource: () => sourceRef.current,
        onMicFailure: setMicError,
      });
      moduleRef.current = module;
      return module;
    },
  });
  const { state: status, phase, fps, captureSampleRate, isNative } = session;

  const sessionState = SESSION_BY_STATUS[status];
  const isStopped = sessionState === 'idle';
  const isTransitioning = phase === 'transitioning';

  // 라이브 인디케이터 타이밍 — 훅은 fps/status만 노출하므로 여기서 추적한다.
  const [now, setNow] = useState(0);
  const startedAtRef = useRef<number | null>(null);
  const lastFrameAtRef = useRef<number | null>(null);
  const fpsRef = useRef(0);
  fpsRef.current = fps;

  // 마이크 실패는 다음 시도까지만 남는다 — 권한을 허용하고 다시 시작했는데도 예전 오류가
  // 붙어 있으면 화면이 거짓을 말한다.
  useEffect(() => {
    if (sessionState !== 'idle') {
      setMicError(null);
    }
  }, [sessionState]);

  // 세션이 살아있는 동안만 시작시각을 잡고, idle로 내려가면 비운다.
  useEffect(() => {
    if (sessionState === 'idle') {
      startedAtRef.current = null;
      lastFrameAtRef.current = null;
      return;
    }
    if (startedAtRef.current === null) {
      startedAtRef.current = Date.now();
    }
  }, [sessionState]);

  // 벽시계 틱(대기 중엔 멈춤) — 프레임이 흐르는 동안 마지막 프레임 시각을 갱신.
  useEffect(() => {
    if (sessionState === 'idle') {
      return;
    }
    setNow(Date.now());
    const id = window.setInterval(() => {
      const t = Date.now();
      setNow(t);
      if (fpsRef.current > 0) {
        lastFrameAtRef.current = t;
      }
    }, TICK_MS);
    return () => window.clearInterval(id);
  }, [sessionState]);

  /*
   * 튜닝값을 **돌고 있는** 세션에 적용한다. `usePitchSession`은 transport를 mount-once로
   * 잡고 config를 ref로 읽으므로, config 객체가 바뀌어도 그것만으로는 엔진에 닿지 않는다
   * — 여기서 명시적으로 updateConfig를 불러야 한다. 대기 중(idle)일 때는 부르지 않는다:
   * 다음 start가 같은 `analysis`로 열리므로 불필요하고, 네이티브는 분석기가 없는 상태에서
   * 에러를 낸다. 드래그 thrash 방지로 짧게 debounce.
   */
  const isFirstApply = useRef(true);
  useEffect(() => {
    if (isFirstApply.current) {
      isFirstApply.current = false;
      return;
    }
    if (sessionState === 'idle') {
      return;
    }
    const timer = window.setTimeout(() => {
      const patch: Partial<PitchTrackerConfig> = { analysis };
      if (isNative) {
        void native.invoke('updateConfig', patch);
        return;
      }
      // 브라우저: **세션이 실제로 쓰는** mock 모듈에 적용한다. 예전엔 튜닝 패널이 자기만의
      // 모듈을 새로 만들어 거기에 적용했다 — 그 인스턴스는 세션과 무관해서 슬라이더를
      // 굴려도 화면이 그대로였다(진단 패널에서 고친 것과 같은 계열).
      void moduleRef.current?.updateConfig(patch);
    }, CONFIG_DEBOUNCE_MS);
    return () => window.clearTimeout(timer);
  }, [analysis, isNative, sessionState]);

  // 엔진 오류로 세션이 중단되면 토스트로 즉시 노출(#23) — 콘솔 어딜 보든 놓치지 않게.
  useEffect(() => {
    if (phase !== 'error') {
      return;
    }
    toast.error('엔진 오류로 세션이 중단됐습니다. 다시 시작해 주세요.');
  }, [phase]);

  const run = (command: SessionCommand): void => {
    switch (command) {
      case 'start':
        session.start();
        return;
      case 'stop':
        session.stop();
        return;
      case 'pause':
        session.pause();
        return;
      case 'resume':
        session.resume();
        return;
    }
  };

  const liveness = deriveLiveness(
    {
      session: sessionState,
      startedAtMs: startedAtRef.current,
      lastFrameAtMs: lastFrameAtRef.current,
    },
    now,
  );

  const value: SessionControlValue = {
    isNative,
    algorithm,
    setAlgorithm,
    analysis,
    setAnalysis,
    source,
    setSource,
    micError,
    sessionState,
    captureSampleRate,
    fps,
    liveness,
    can: {
      pause: session.can.pause,
      resume: session.can.resume,
      algorithm: status === 'idle',
      source: status === 'idle',
    },
    isStopped,
    isTransitioning,
    run,
    getDiagnostics: () => moduleRef.current?.getDiagnosticsSnapshot() ?? null,
  };

  return (
    <SessionControlContext.Provider value={value}>
      {children}
    </SessionControlContext.Provider>
  );
};

export const useSessionControl = (): SessionControlValue => {
  const value = useContext(SessionControlContext);
  if (!value) {
    throw new Error(
      'useSessionControl must be used within a SessionControlProvider',
    );
  }
  return value;
};
